<template>
  <li
    :ref="isSponsoredItem ? sponsoredItemRefs[product?.id] : null"
    :class="['cell sortablegrid-product', cellClasses, { 'featured-item': isSponsoredItem }]"
    :data-liIndex="index"
    :data-product-id="isSponsoredItem ? product?.id : null"
    :data-product-name="isSponsoredItem ? product?.detail?.name : null"
    @mouseover="setImageHover({ action: 'start', productId: product?.id || null })"
    @mouseleave="setImageHover({ action: 'end', productId: product?.id || null })"
  >
    <QuickshopButton
      v-if="isDiscoveryGridQuickShopEnabled && isProductNotExcluded(product) && mounted"
      v-bind="{ product }"
    />
    <ProductThumbnail
      :product="product"
      :url-template="urlTemplate"
      :image-width="pictureProps?.imageWidth"
      :retina-adjustment="pictureProps?.retinaAdjustment"
      :retina-quality="pictureProps?.retinaQuality"
      :fetch-priority="isEagerLoaded"
      :lazy-load="isLazyLoaded"
      :hide-review-text-and-rating="true"
      :show-price-type-above-price="showPriceTypeAbovePrice"
      :special-offers-badge="specialOffersBadge"
      :slideshow-props="slideshowProps"
      :skeleton="isLazyLoaded"
      :open-in-new-tab="deviceType?.isDesktop"
      :skeleton-options="skeletonOptions"
      :first-image-priority="true"
      :preload-second-image="isAlternativeImagesViewEnabled && deviceType?.isDesktop"
      :is-simplification-on="isSimplificationOn"
      :is-sortable-grid="enableH3ProductTitlesSortableGrid"
      :simplification-with-v1="simplificationWithV1"
      :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
      :simplification-remove-sale-exp="simplificationRemoveSaleExp"
      :selected-facet-sizes="selectedFilters"
      :first-picture-native-lazy="true"
      :loyalty-badge="loyaltyBadge"
      :color-swatch-enhancement="colorSwatchEnhancement"
      :device-type="deviceType?.deviceType"
      :macys-exclusive-badging="macysExclusiveBadging"
      :bcom-small-swatches="bcomSmallSwatches"
      :use-applied-mcom-copy-exp="useAppliedMcomCopyExp"
      :is-small-alt-grid-view-card="isSmallAltGridViewCard"
      :price-type-badge-exp="priceTypeBadgeExp"
      :mcom-font-weight="mcomFontWeight"
      :value-gift-badge-enabled="valueGiftBadgeEnabled"
      @chevronDirection="emit('chevronDirection', $event)"
      @updateSelectedColorId="updateSelectedColorId(product.id, $event)"
    />
  </li>
</template>

<script setup>
/* istanbul ignore file */
import {
  computed, ref, watch, onBeforeUnmount,
} from 'vue';
import { useMounted } from '@vueuse/core';
import { ProductThumbnail, useSpecialOffersBadge } from '@common-vue/product-thumbnail';
import { brand } from 'config';
import useKillSwitches from '@composables/useKillSwitches';

const isBcom = brand === 'bcom';
const EXTRA_IMAGE_PARAMS = 'layer=1&bgColor=100,100,100&opac=5';
const urlTemplate = {
  product: isBcom
    ? `https://images.bloomingdalesassets.com/is/image/BLM/products/[IMAGEFILEPATH]?${EXTRA_IMAGE_PARAMS}`
    : `https://slimages.macysassets.com/is/image/MCY/products/[IMAGEFILEPATH]?${EXTRA_IMAGE_PARAMS}`,
  swatch: isBcom
    ? 'https://images.bloomingdalesassets.com/is/image/BLM/swatches/'
    : 'https://slimages.macysassets.com/is/image/MCY/swatches/',
  swatchSprite: isBcom
    ? 'https://images.bloomingdalesassets.com/is/image/BLM/'
    : 'https://slimages.macysassets.com/is/image/MCY/',
};
import useFeatureEligibility from '@modules/featureControl/composables/useFeatureEligibility';
import { useProductThumbnailCss, useProductQuickviewSlideout, useAltGridView } from '../composables';
import QuickshopButton from '../../../components/QuickshopButton/QuickshopButton';

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  enableLazyForEagerLoaded: {
    type: Boolean,
    required: false,
  },
  pictureProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  deviceType: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  pzoBadgingEnabled: {
    type: Boolean,
    required: false,
  },
  pzoItems: {
    type: Array,
    required: false,
  },
  isSimplificationOn: {
    type: Boolean,
    default: false,
  },
  simplificationWithV1: {
    type: Boolean,
    default: false,
  },
  simplificationNonBoldPercentageExp: {
    type: Boolean,
    default: false,
  },
  simplificationRemoveSaleExp: {
    type: Boolean,
    default: false,
  },
  selectedFilters: {
    type: Array,
    default: () => ([]),
  },
  loyaltyBadge: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  isAlternativeImagesViewEnabled: {
    type: Boolean,
    default: false,
  },
  isAlternativeImagesViewExp3Enabled: {
    type: Boolean,
    default: false,
  },
  colorSwatchEnhancement: {
    type: Boolean,
    required: false,
    default: false,
  },
  disablePzoBadges: {
    type: Boolean,
    required: false,
    default: false,
  },
  macysExclusiveBadging: {
    type: Boolean,
    required: false,
    default: false,
  },
  bcomSmallSwatches: {
    type: Boolean,
    required: false,
    default: false,
  },
  useAppliedMcomCopyExp: {
    type: Boolean,
    default: false,
  },
  priceTypeBadgeExp: {
    type: Boolean,
    default: false,
  },
  mcomFontWeight: {
    type: Boolean,
    default: false,
  },
  valueGiftBadgeEnabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['getSponsoredItemRefs', 'chevronDirection']);

const cellClasses = useProductThumbnailCss(props.index);

const { setSelectedColorSwatchId, setImageHover, isProductNotExcluded } = useProductQuickviewSlideout();

const mounted = useMounted();

const { enableH3ProductTitlesSortableGrid } = useKillSwitches();
const { isDiscoveryGridQuickShopEnabled } = useFeatureEligibility();

const updateSelectedColorId = (productId, $event) => {
  setSelectedColorSwatchId(productId, $event);
};

const { isSmallAltGridViewCard } = useAltGridView();

// slideshow indicator should be visible only for DESKTOP MCOM
const showSlideshowIndicator = props.isAlternativeImagesViewEnabled && !props.isAlternativeImagesViewExp3Enabled && props.deviceType.isDesktop && brand === 'mcom';
const slideshowProps = {
  useSlideshow: true,
  autoplayMode: props.isAlternativeImagesViewEnabled && props.deviceType.isDesktop ? 'hover-2-slides' : 'manual',
  showIndicator: showSlideshowIndicator,
  showIndicatorOnSlideshow: showSlideshowIndicator,
  backToFirst: props.isAlternativeImagesViewEnabled ? props.deviceType.isDesktop : true,
  renderSlideshowOnMount: props.isAlternativeImagesViewEnabled, // render only first product image on SSR to reduce initial HTML
  disableSlideshow: !props.isAlternativeImagesViewEnabled && !props.deviceType.isDesktop,
  showSkeleton: false,
  autoplayInterval: 800,
  hideSlideshowButtons: props.isAlternativeImagesViewExp3Enabled,
};
const skeletonOptions = {
  width: '100%',
  height: '0px',
  paddingBottom: brand === 'mcom' ? '122%' : '125%', // based on aspect ratio of 0.82 for mcom and 0.8 for bcom
};

/* Only the first PT should be fetchpriority='high' */
const shouldBeFetchPriorityHigh = props.index < 1;
const showPriceTypeAbovePrice = false;

// Corner Badge
const specialOffersBadge = ref({});
const specialOfferBadgeOptions = {
  priceTypeBadgeExp: props?.priceTypeBadgeExp,
};
const { getSpecialOffersBadgeSettings } = useSpecialOffersBadge(specialOfferBadgeOptions);
const badgeText = props.product?.pricing?.price?.priceType?.text || '';
specialOffersBadge.value = getSpecialOffersBadgeSettings(badgeText, brand);

watch(() => props.pzoItems, (newPzoItems, oldPzoItems) => {
  /* istanbul ignore else */
  if (props.pzoBadgingEnabled && newPzoItems !== oldPzoItems) {
    /* istanbul ignore else */
    if (newPzoItems.includes(props.product?.id)) {
      specialOffersBadge.value = {
        enableBadge: !props?.disablePzoBadges,
        badgeText: 'Exclusive offer in bag',
        classesApplied: 'pzoBadging',
      };
    }
  }
});

/* isEagerLoad: fetchPriority='high' for the first PT
  isLazyLoad: loading='eager' for the first 4 PT
  else loading = 'lazy' */
const isEagerLoaded = computed(() => shouldBeFetchPriorityHigh);
const isLazyLoaded = computed(() => props.enableLazyForEagerLoaded || props.index > 3);
const isSponsoredItem = ref(props.product?.detail?.flags?.siteMonetizationProduct || false);
const sponsoredItemRefs = {};
/* istanbul ignore else */
if (isSponsoredItem.value) {
  sponsoredItemRefs[props.product?.id] = ref(null);
  emit('getSponsoredItemRefs', { key: props.product?.id, value: sponsoredItemRefs[props.product?.id], analyticBeacons: props.product?.meta?.productSMData });
}

onBeforeUnmount(() => {
  /* istanbul ignore else */
  if (isSponsoredItem.value) {
    emit('getSponsoredItemRefs', { key: props.product?.id, value: null, analyticBeacons: null });
  }
});
</script>

<style lang="scss">
.sortablegrid-product {
  position: relative;

  .corner-badge.pzoBadging {
    font-size: 12px;
    background-color: #F6D96A;
    font-weight: 700;
    line-height: 18px;
  }
}

.small-alt-grid-view .bottom-overlay-wrapper {
  margin-top: 0;
  position: relative;

  .bottom-overlay-label {
    position: absolute !important;
  }
}
</style>
